import React, { useEffect, useRef, useState } from 'react';
import { Tag, Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import type { MerchantDomain, DomainCardProps } from '../types';
import { DOMAIN_STATUS_CONFIG, DOMAIN_TYPE_LABELS, getPreferredCNAME, getPreferredIPv4List } from '../constants';
import CopyableCode from './CopyableCode';
import ConfirmDialog from '../../../commonComponents/ConfirmDialog';
import { useTheme } from '../../../context/ThemeContext';
import { ActionIconButton } from '../../Merchants/styles';
import {
  DomainCardWrapper,
  DomainCardTopRow,
  DomainCardMetaRow,
  DomainName,
  DomainGlobalIcon,
  DomainCardAlert,
  DnsSetupCard,
  DnsSetupHeader,
  DnsSetupIcon,
  DnsStepList,
  DnsStep,
  DnsStepBadge,
  DnsStepBody,
  DnsStepTitle,
  DnsFieldRow,
  DnsFieldPill,
} from '../Styles';

const StatusTag: React.FC<{ status: MerchantDomain['status'] }> = ({ status }) => {
  const cfg = DOMAIN_STATUS_CONFIG[status];
  if (!cfg) return null;
  return <Tag icon={<cfg.Icon />} color={cfg.color}>{cfg.label}</Tag>;
};

const DomainTypeTag: React.FC<{ domainType: MerchantDomain['domainType'] }> = ({ domainType }) => (
  <Tag>{DOMAIN_TYPE_LABELS[domainType] ?? domainType}</Tag>
);

const DnsSetupInstructions: React.FC<{ domain: MerchantDomain }> = ({ domain }) => {
  const { dnsRecords, verificationRecords, domainType } = domain;
  const verificationRecord = verificationRecords?.[0];
  const cname = getPreferredCNAME(dnsRecords);
  const ipv4List = getPreferredIPv4List(dnsRecords);
  const hasRouting = domainType === 'subdomain' ? !!cname : ipv4List.length > 0;

  const steps: { title: string; type: string; host: string; values: string[] }[] = [];

  if (verificationRecord) {
    steps.push({
      title: 'Ownership Verification',
      type: verificationRecord.type,
      host: verificationRecord.domain,
      values: [verificationRecord.value],
    });
  }

  if (hasRouting) {
    steps.push(
      domainType === 'subdomain'
        ? { title: 'Routing', type: 'CNAME', host: domain.domain.split('.')[0], values: [cname!] }
        : { title: 'Routing', type: 'A', host: '@', values: ipv4List },
    );
  }

  return (
    <DnsSetupCard>
      <DnsSetupHeader>
        <DnsSetupIcon />
        <span>Configure DNS at your domain provider</span>
      </DnsSetupHeader>
      <DnsStepList>
        {steps.map((step, index) => (
          <DnsStep key={step.title}>
            {steps.length > 1 && <DnsStepBadge>{index + 1}</DnsStepBadge>}
            <DnsStepBody>
              <DnsStepTitle>{step.title}</DnsStepTitle>
              <DnsFieldRow>
                <DnsFieldPill><strong>Type</strong> {step.type}</DnsFieldPill>
                <DnsFieldPill><strong>Host</strong> {step.host}</DnsFieldPill>
              </DnsFieldRow>
              {step.values.map((value) => (
                <CopyableCode key={value} value={value} />
              ))}
            </DnsStepBody>
          </DnsStep>
        ))}
      </DnsStepList>
    </DnsSetupCard>
  );
};

const DomainCard: React.FC<DomainCardProps> = ({ domain, onRemove, removing }) => {
  const { isDarkMode } = useTheme();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const wasRemovingRef = useRef(removing);

  useEffect(() => {
    if (wasRemovingRef.current && !removing) {
      setIsConfirmOpen(false);
    }
    wasRemovingRef.current = removing;
  }, [removing]);

  return (
    <DomainCardWrapper>
      <DomainCardTopRow>
        <DomainCardMetaRow>
          <DomainGlobalIcon />
          <DomainName>{domain.domain}</DomainName>
          <DomainTypeTag domainType={domain.domainType} />
          <StatusTag status={domain.status} />
        </DomainCardMetaRow>
        <Tooltip title="Remove domain">
          <ActionIconButton
            iconColor="#f5222d"
            disabled={removing}
            onClick={() => !removing && setIsConfirmOpen(true)}
          >
            <FontAwesomeIcon icon={faTrash} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
          </ActionIconButton>
        </Tooltip>
      </DomainCardTopRow>

      <ConfirmDialog
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={onRemove}
        title="Remove Domain"
        message={`Are you sure you want to remove "${domain.domain}"? DNS records you've set will remain — you'll need to remove them manually from your provider.`}
        confirmText="Remove"
        cancelText="Cancel"
        confirmColorScheme="red"
        loading={removing}
      />

      {domain.status === 'pending_approval' && (
        <DomainCardAlert
          type="info"
          showIcon
          message="Awaiting admin approval"
          description="Your domain has been submitted and is waiting for review. You'll be able to configure DNS once it's approved."
        />
      )}

      {domain.status === 'pending_verification' && (
        <DnsSetupInstructions domain={domain} />
      )}

      {domain.status === 'error' && domain.errorMessage && (
        <DomainCardAlert
          type="error"
          showIcon
          message="Domain error"
          description={domain.errorMessage}
        />
      )}
    </DomainCardWrapper>
  );
};

export default DomainCard;
